import { Toast } from "primereact/toast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import TransporterInfoForm from "../components/TransporterInfoForm";
import { useTransporterRefresh } from "../hooks/useTransporterRefresh";
import {
  SubscriptionType,
  Transporter,
} from "../interfaces/transporters.interface";
import { authService } from "../service/AuthService";
import { DriverService } from "../service/DriverService";

import "./Parameters.css";

const Parameters: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [accountInfo, setAccountInfo] = useState<Partial<Transporter> | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refreshTransporter } = useTransporterRefresh();

  const fetchAccountInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      const user = await refreshTransporter();
      setAccountInfo(user);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des informations du compte:",
        error
      );
      showToast(
        "error",
        "Erreur",
        "Échec de la récupération des informations du compte"
      );
      setError("Échec de la récupération des informations du compte");
    } finally {
      setIsLoading(false);
    }
  }, [refreshTransporter]);

  useEffect(() => {
    fetchAccountInfo();
  }, [fetchAccountInfo]);

  const handleSubmit = async (
    updatedData: Partial<Transporter>,
    logoFile?: File
  ) => {
    try {
      setIsLoading(true);
      const updatedUser = await authService.updateTransporter(
        updatedData,
        logoFile
      );
      setAccountInfo(updatedUser);
      await fetchAccountInfo(); // Refresh data after update
      showToast(
        "success",
        "Succès",
        "Informations du compte mises à jour avec succès"
      );
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des informations du compte:",
        error
      );
      showToast(
        "error",
        "Erreur",
        "Échec de la mise à jour des informations du compte"
      );
      setError("Échec de la mise à jour des informations du compte");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeSubscription = async () => {
    try {
      setIsLoading(true);
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

      const updatedSubscription: Partial<Transporter> = {
        subscriptionType: SubscriptionType.PAID,
        endDate: oneYearFromNow,
      };

      const updatedUser = await authService.updateTransporterSubscription(
        updatedSubscription
      );
      setAccountInfo(updatedUser);
      await fetchAccountInfo(); // Refresh data after subscription update
      showToast("success", "Succès", "Abonnement mis à niveau avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à niveau de l'abonnement:", error);
      showToast("error", "Erreur", "Échec de la mise à niveau de l'abonnement");
      setError("Échec de la mise à niveau de l'abonnement");
    } finally {
      setIsLoading(false);
    }
  };

  const getRemainingDriverSlots = useCallback(async (): Promise<
    number | null
  > => {
    const driverService = new DriverService();
    if (accountInfo && accountInfo._id) {
      try {
        return await driverService.getRemainingDriverSlots(accountInfo._id);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des places de conducteurs restantes:",
          error
        );
        showToast(
          "error",
          "Erreur",
          "Échec de la récupération des places de conducteurs restantes"
        );
        return null;
      }
    }
    return null;
  }, [accountInfo]);

  const showToast = (
    severity: "success" | "error",
    summary: string,
    detail: string
  ) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
      contentClassName: "custom-toast",
    });
  };

  if (!accountInfo) {
    return (
      <div className="flex items-center justify-center h-screen">
        Chargement...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-70px)] bg-slate-50">
      <Toast ref={toast} />
      <div className="px-12 flex flex-col h-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Paramètres</h1>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto pr-4 -mr-4">
            <div className="space-y-6 pb-6">
              <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-2xl font-semibold my-0 mb-6">
                  Modifier vos informations
                </h2>
                <TransporterInfoForm
                  onSubmit={handleSubmit}
                  onUpgradeSubscription={handleUpgradeSubscription}
                  initialData={accountInfo}
                  isLoading={isLoading}
                  error={error}
                  getRemainingDriverSlots={getRemainingDriverSlots}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Parameters;
